import { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from "@mui/material";

/**
 * "New tutorial available" prompt. Purely presentational; the manager
 * decides when to show it and what the buttons persist.
 */
export const TutorialPromptDialog = ({ open, tutorial, onStart, onSkip, mandatory = false }) => {
  const [suppress, setSuppress] = useState(false);

  useEffect(() => {
    if (open) setSuppress(false);
  }, [open]);

  if (!tutorial) return null;

  return (
    <Dialog
      open={open}
      // Deliberate: a click outside must NOT dismiss the prompt (users were
      // "skipping" tutorials without ever seeing them). Esc acts like "Not
      // now" — harmless, since that no longer persists anything.
      onClose={(_e, reason) => {
        if (reason === "backdropClick") return;
        onSkip({ suppress });
      }}
      maxWidth="xs"
      fullWidth
      data-testid="tutorial-prompt"
      PaperProps={{
        sx: {
          background: "#1F2A3B",
          color: "#F7FAFA",
          borderRadius: "14px",
          border: "1px solid rgba(255,255,255,0.1)",
        },
      }}
    >
      <DialogTitle sx={{ fontSize: 18, fontWeight: 700, pb: 1 }}>
        <Typography component="span" sx={{ display: "block", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", color: mandatory ? "#FF9AA4" : "#F5C754", textTransform: "uppercase", mb: 0.5 }}>
          {mandatory ? "Required tutorial" : "New tutorial"}
        </Typography>
        {tutorial.title}
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        <Typography sx={{ fontSize: 14, color: "rgba(247,250,250,0.82)", lineHeight: 1.55 }}>
          {tutorial.summary}
        </Typography>
        <Typography sx={{ fontSize: 12, color: "rgba(247,250,250,0.55)", mt: 1 }}>
          {tutorial.mode === "article" ? "Short read" : "Guided tour"} · about {tutorial.estimatedMinutes} min. You can
          find it later under Tutorials.
        </Typography>
        {mandatory && (
          <Typography sx={{ fontSize: 13, color: "#FF9AA4", mt: 1 }} data-testid="tutorial-prompt-required">
            Your newsroom marked this one as required — it will be offered again until you finish it.
          </Typography>
        )}
        {!mandatory && (
        <FormControlLabel
          sx={{ mt: 1.5, ml: 0, "& .MuiTypography-root": { fontSize: 13, color: "rgba(247,250,250,0.75)" } }}
          control={
            <Checkbox
              size="small"
              checked={suppress}
              onChange={(e) => setSuppress(e.target.checked)}
              data-testid="tutorial-prompt-suppress"
              sx={{ color: "rgba(247,250,250,0.5)", "&.Mui-checked": { color: "#F5C754" } }}
            />
          }
          label="Don't show tips about new features again"
        />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          onClick={() => onSkip({ suppress })}
          data-testid="tutorial-prompt-skip"
          sx={{ textTransform: "none", color: "rgba(247,250,250,0.8)", fontWeight: 600 }}
        >
          Not now
        </Button>
        <Button
          variant="contained"
          onClick={() => onStart({ suppress })}
          data-testid="tutorial-prompt-start"
          sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", background: "#F5C754", color: "#1B2432", "&:hover": { background: "#FFD873" } }}
        >
          {tutorial.mode === "article" ? "Read it" : "Take the tour"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
